import {
	signInWithPopup,
	signOut,
	FacebookAuthProvider,
	signInWithEmailAndPassword,
	GoogleAuthProvider,
	TwitterAuthProvider,
	createUserWithEmailAndPassword,
	sendPasswordResetEmail,
	updatePassword,
	deleteUser,
} from 'firebase/auth';

import { resetAuthData, signInUser, signUpUser } from '../actions/Auth.actions';
import { getUserInfo } from '../actions/User.actions';
import { AuthTypes } from '../constants/Types';
import { email } from '../constants/Validations';
import { store } from '../store/Store';
import { auth } from './Config';

const handleSSOLogin = (
	authType: AuthTypes,
	user: {
		userName?: string | null;
		name?: string | null;
		email?: string | null;
		profilePicture?: string | null;
	},
	successCallBack?: (data: any) => void,
	errorCallBack?: () => void
) => {
	store.dispatch(
		signInUser({ ...user, authType }, successCallBack, errorCallBack)
	);
};

const signInWithEmailPWD = (
	email: string,
	password: string,
	successCallBack?: (data: any) => void,
	errorCallBack?: (error?: string) => void
) =>
	signInWithEmailAndPassword(auth, email, password)
		.then(() => {
			handleSSOLogin(AuthTypes.EMAIL, { email }, successCallBack, errorCallBack);
		})
		.catch(() => {
			errorCallBack?.('Wrong password or user does not exist!');
		});

export const signInWithSSO = (
	authType: AuthTypes,
	successCallBack?: (data: any) => void,
	errorCallBack?: () => void,
	userName?: string | null
) => {
	let provider;
	if (authType === AuthTypes.FACEBOOK) {
		provider = new FacebookAuthProvider();
	} else if (authType === AuthTypes.GOOGLE) {
		provider = new GoogleAuthProvider();
	} else if (authType === AuthTypes.TWITTER) {
		provider = new TwitterAuthProvider();
	}

	signInWithPopup(auth, provider)
		.then(result => {
			const user = result.user;
			handleSSOLogin(
				authType,
				{
					userName: userName ? userName : user.displayName,
					name: user.displayName,
					email: user.email,
					profilePicture: user.photoURL?.replace('=s96-c', ''),
				},
				successCallBack,
				errorCallBack
			);
		})
		.catch(error => {
			console.error(error);
			errorCallBack?.();
		});
};

export const signIn = (
	userName?: string,
	password?: string,
	successCallBack?: (data: any) => void,
	errorCallBack?: (error?: string) => void
) => {
	if (userName && password) {
		if (email(userName)) {
			signInWithEmailPWD(userName, password, successCallBack, errorCallBack);
		} else {
			store.dispatch(
				getUserInfo({ userName }, user => {
					if (user?.email) {
						signInWithEmailPWD(user.email, password, successCallBack, errorCallBack);
					} else {
						errorCallBack?.('User does not exist!');
					}
				})
			);
		}
	} else {
		errorCallBack?.('All fields are required!');
	}
};

export const signOutFromApp = (callback?: () => void) =>
	signOut(auth)
		.then(() => {
			store.dispatch(resetAuthData());
			callback?.();
		})
		.catch(err => {
			console.log(err);
		});

export const signUp = (
	userName: string,
	email: string,
	password: string,
	token: null | undefined | string,
	successCallBack: (data: any) => void,
	errorCallBack: (error?: string) => void
) => {
	store.dispatch(
		getUserInfo({ userName }, user => {
			if (!user) {
				createUserWithEmailAndPassword(auth, email, password)
					.then(() => {
						store.dispatch(
							signUpUser({ userName, email, token }, successCallBack, errorCallBack)
						);
					})
					.catch(() => {
						errorCallBack('Email already exists!');
					});
			} else {
				errorCallBack('User already exists!');
			}
		})
	);
};

export const getValidToken = async () => {
	const token = await auth.currentUser?.getIdToken(true);
	return token;
};

export const resetForgotPassword = (
	email: string,
	successCallBack?: (res: any) => void,
	errorCallBack?: (error?: any) => void
) => {
	sendPasswordResetEmail(auth, email)
		.then(res => {
			successCallBack?.(res);
		})
		.catch(err => {
			errorCallBack?.(err);
		});
};

export const updateCurrentPassword = (
	newPassword: string,
	successCallBack?: (res: any) => void,
	errorCallBack?: (error?: string) => void
) => {
	const user = auth.currentUser;
	if (user) {
		updatePassword(user, newPassword)
			.then(res => {
				successCallBack?.(res);
			})
			.catch(error => {
				errorCallBack?.(error);
			});
	} else {
		errorCallBack?.('User does not exist!');
	}
};

export const deleteUserFromApp = (
	successCallBack?: (res: any) => void,
	errorCallBack?: (error?: string) => void
) => {
	const user = auth.currentUser;
	if (user) {
		deleteUser(user)
			.then(res => {
				successCallBack?.(res);
			})
			.catch(error => {
				errorCallBack?.(error);
			});
	} else {
		errorCallBack?.('User does not exist!');
	}
};
